import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { getCookie, setCookie, setAdminAuthToken, restoreAdminSessionIfNeeded } from './utils/cookies';
import { ErrorNotificationProvider } from './contexts/ErrorNotificationContext';
import ProtectedAdminRoute from './components/ProtectedAdminRoute/ProtectedAdminRoute';

// Lazy load heavy components for code splitting
const AdminDashboard = lazy(() =>
    import(/* webpackChunkName: "admin-dashboard" */ './components/AdminDashboard/AdminDashboard')
);
const AdminHome = lazy(() => import('./pages/Admin/Home/AdminHome'));
const AdminProfile = lazy(() => import('./pages/Admin/Profile/AdminProfile'));
const Users = lazy(() => import('./pages/Admin/Users/Users'));
const Modules = lazy(() => import('./pages/Admin/Modules/Modules'));
const Gamification = lazy(() => import('./pages/Admin/Gamification/Gamification'));
const Updates = lazy(() => import('./pages/Admin/Updates/Updates'));
const Super = lazy(() => import('./pages/Admin/Super/Super'));
const AdminLogin = lazy(() => import('./pages/Admin/Login/index'));
const VerifyAdmin = lazy(() => import('./pages/Admin/Login/VerifyAdmin'));
const SignupHome = lazy(() => import('./pages/Admin/Signup/index'));
const VerifySignup = lazy(() => import('./pages/Admin/Signup/VerifySignup'));
const RegisterForm = lazy(() => import('./pages/Admin/Signup/RegisterForm'));
const PostReviewPage = lazy(() => import('./pages/PostReview/PostReviewPage'));
const VerifyPostReview = lazy(() => import('./pages/PostReview/VerifyPostReview'));
// import Email from "./pages/Admin/Email/index";




function scheduleIdleTask(callback) {
    if (typeof window.requestIdleCallback === 'function') {
        return { kind: 'idle', id: window.requestIdleCallback(callback, { timeout: 2000 }) };
    }
    return { kind: 'timeout', id: window.setTimeout(callback, 1) };
}

function cancelIdleTask(handle) {
    if (!handle) return;
    if (handle.kind === 'idle' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(handle.id);
    } else {
        clearTimeout(handle.id);
    }
}

function AnalyticsTracker() {
    const location = useLocation();

    useEffect(() => {
        const handle = scheduleIdleTask(() => {
            if (window.gtag) {
                window.gtag('config', 'G-ZBKK9LV0ZJ', {
                    page_path: location.pathname,
                });
            }
        });
        return () => cancelIdleTask(handle);
    }, [location.pathname]);

    return null;
}

function App() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e); // Save the event for later
           // console.log('📢 Install prompt is ready!');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        window.addEventListener('appinstalled', () => {
           // console.log('✅ PWA installed');
           // setIsInstalled(true);
        });

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(null); // Start with null
    const [loading, setLoading] = useState(true); // Loading state

    // ✅ Memoize checkAuth to prevent recreating on every render
    const checkAuth = useCallback(() => {
        // If user switch wiped shared localhost cookies, restore from port-scoped backup
        restoreAdminSessionIfNeeded();

        let adminToken = getCookie('admin_id');

        // Allow cross-app switch flow (user -> admin) by bootstrapping cookies from URL.
        if (!adminToken) {
            const currentPath = window.location.pathname.toLowerCase();
            const isAdminPath = currentPath.startsWith('/admindashboard');
            if (isAdminPath) {
                const urlParams = new URLSearchParams(window.location.search);
                const adminIdFromUrl = urlParams.get('admin_id');
                const companyIdFromUrl = urlParams.get('company_id');
                const tokenFromUrl = urlParams.get('token');

                if (adminIdFromUrl) {
                    setCookie('admin_id', adminIdFromUrl, 90);
                    if (companyIdFromUrl) {
                        setCookie('company_id', companyIdFromUrl, 90);
                    }
                    if (tokenFromUrl) {
                        setAdminAuthToken(decodeURIComponent(tokenFromUrl), 90);
                    }

                    // Clean query params after cookies are persisted.
                    window.history.replaceState({}, document.title, window.location.pathname);
                    adminToken = adminIdFromUrl;
                }
            }
        }

        const isAuth = !!adminToken;
        // ✅ Only update state if it actually changed to prevent unnecessary re-renders
        setIsAdminAuthenticated(prev => {
            if (prev !== isAuth) {
                return isAuth;
            }
            return prev;
        });
        setLoading(false); // Authentication check is complete
    }, []);

    useEffect(() => {
        // ✅ Check immediately without any delay
        checkAuth();
        
        // ✅ Listen to storage events for cross-tab updates
        window.addEventListener('storage', checkAuth);

        // ✅ Listen for custom auth update events (triggered after login/signup)
        // Use a debounced handler to prevent multiple rapid updates
        let timeoutId;
        const handleAuthUpdate = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                checkAuth();
            }, 50); // Increased delay to ensure cookies are processed by browser
        };
        window.addEventListener('authUpdated', handleAuthUpdate);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('storage', checkAuth);
            window.removeEventListener('authUpdated', handleAuthUpdate);
        };
    }, [checkAuth]);

    if (loading) return <div role="status" aria-live="polite" aria-label="Loading application">Loading...</div>;

    return (
        <ErrorNotificationProvider>
        <Router>
            <AnalyticsTracker />
            <Routes>
                <Route path="/login" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                        <AdminLogin deferredPrompt={deferredPrompt} />
                    </Suspense>
                } />
                <Route path="/verify-admin" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                        <VerifyAdmin />
                    </Suspense>
                } />
                <Route path="/signup" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                        <SignupHome />
                    </Suspense>
                } />
                <Route path="/verify-signup" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                        <VerifySignup />
                    </Suspense>
                } />
                <Route path="/register-form" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                        <RegisterForm />
                    </Suspense>
                } />
                {/* Public post edit/approve invite (no admin login) */}
                <Route path="/review/:token/verify" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Verifying…</div>}>
                        <VerifyPostReview />
                    </Suspense>
                } />
                <Route path="/review/:token" element={
                    <Suspense fallback={<div role="status" aria-live="polite">Loading review…</div>}>
                        <PostReviewPage />
                    </Suspense>
                } />
                {/* ✅ Admin Routes with `Outlet` */}
                <Route
                    path="/admindashboard"
                    element={
                        <ProtectedAdminRoute isAuthenticated={isAdminAuthenticated}>
                            <Suspense fallback={<div role="status" aria-live="polite">Loading dashboard…</div>}>
                                <AdminDashboard />
                            </Suspense>
                        </ProtectedAdminRoute>
                    }
                >
                    <Route index element={<Navigate to="adminhome" />} />
                    <Route path="adminhome" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <AdminHome />
                        </Suspense>
                    } />
                    <Route path="adminprofile" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <AdminProfile />
                        </Suspense>
                    } />
                    <Route path="users" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <Users />
                        </Suspense>
                    } />
                    <Route path="modules" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <Modules />
                        </Suspense>
                    } />
                    <Route path="points" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <Gamification />
                        </Suspense>
                    } />
                    <Route path="updates" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <Updates />
                        </Suspense>
                    } />
                    <Route path="super" element={
                        <Suspense fallback={<div role="status" aria-live="polite">Loading...</div>}>
                            <Super />
                        </Suspense>
                    } />
                    {/* <Route path="email" element={<Email />} /> */}
                </Route>
                <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
        </Router>
        </ErrorNotificationProvider>
    );
}

export default App;
